import { api } from './client';
import type {
  AdminInfo,
  AdminLoginResponse,
  KeysListResponse,
  KeyOperationResponse,
  AdminListResponse,
  AdminOperationResponse,
  AdminRole,
  GitHubAppStatus,
  ReleasePolicyResponse,
  ReleasePolicyOperationResponse,
} from '@/types/admin';
import type { ReleasesListResponse } from './releases';

// --- Admin Auth ---

export async function login(): Promise<AdminLoginResponse> {
  return api.post('/api/admin/auth/login');
}

export async function me(): Promise<AdminInfo> {
  return api.get('/api/admin/auth/me');
}

export async function logout(): Promise<void> {
  return api.post('/api/admin/auth/logout');
}

// --- Admin Keys ---

export async function listKeys(): Promise<KeysListResponse> {
  return api.get('/api/admin/keys');
}

export async function setKey(
  key: string,
  value: string,
  description?: string
): Promise<KeyOperationResponse> {
  return api.put(`/api/admin/keys/${encodeURIComponent(key)}`, {
    value,
    ...(description ? { description } : {}),
  });
}

export async function deleteKey(
  key: string
): Promise<KeyOperationResponse> {
  return api.delete(`/api/admin/keys/${encodeURIComponent(key)}`);
}

export async function configureGitHubApp(config: {
  appId: string;
  installationId: string;
  privateKeyPem: string;
}): Promise<KeyOperationResponse> {
  return api.put('/api/admin/keys/github-app', config);
}

export async function getGitHubAppStatus(): Promise<GitHubAppStatus> {
  return api.get('/api/admin/keys/github-app/status');
}

// --- Admin Management ---

export async function listAdmins(): Promise<AdminListResponse> {
  return api.get('/api/admin/admins');
}

export async function addAdmin(
  email: string,
  role: AdminRole
): Promise<AdminOperationResponse> {
  return api.post('/api/admin/admins', { email, role });
}

export async function removeAdmin(
  email: string
): Promise<AdminOperationResponse> {
  return api.delete(`/api/admin/admins/${encodeURIComponent(email)}`);
}

// --- Admin Releases (pin-version rollback) ---

export async function listAdminReleases(): Promise<ReleasesListResponse> {
  return api.get('/api/admin/releases');
}

export async function getReleasePolicy(): Promise<ReleasePolicyResponse> {
  return api.get('/api/admin/releases/policy');
}

export async function setReleasePolicy(
  pinVersion: string,
  reason?: string
): Promise<ReleasePolicyOperationResponse> {
  return api.put('/api/admin/releases/policy', {
    pinVersion,
    ...(reason ? { reason } : {}),
  });
}

export async function clearReleasePolicy(): Promise<ReleasePolicyOperationResponse> {
  return api.delete('/api/admin/releases/policy');
}
